import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { keyframes } from '@mui/material/styles';
import SpeedIcon from '@mui/icons-material/Speed';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import CloseIcon from '@mui/icons-material/Close';
import { ResponsiveContainer, AreaChart, Area, CartesianGrid, XAxis, YAxis, Tooltip as RechartsTooltip, Legend, BarChart, Bar, Cell, ReferenceDot } from 'recharts';
import { useOptimization } from '../../context/OptimizationContext';
import { RESULT_ALGORITHM_COLORS, RESULT_ALGORITHM_NAMES, ResultAlgorithmKey, getResultAlgorithmKeysForResults } from '../../utils/algorithmRegistry';
import { ChartDataPoint } from '../../types';

const NAME_TO_KEY: Record<string, ResultAlgorithmKey> = {
  GA: 'ga',
  Cultural: 'culture',
  SFLA: 'frog',
  'SFLA-GA': 'sflaGa',
  SFLAGA: 'sflaga',
  PSO: 'pso',
  GAPSO: 'gapso',
};

const FALLBACK_COLORS = ['#5c6bc0', '#26c6da', '#ef6c00', '#8e24aa', '#2e7d32', '#ff7043', '#00897b'];

const PANEL_BG = '#ffffff';
const PANEL_BG_SUBTLE = '#f8fafc';
const PANEL_BORDER = 'rgba(15,23,42,0.08)';
const TEXT_PRIMARY = '#0f172a';
const TEXT_SECONDARY = '#475569';
const GRID_STROKE = 'rgba(148,163,184,0.28)';
const AXIS_STROKE = '#64748b';
const SOFT_FILL = 'rgba(148,163,184,0.14)';
const SOFT_BORDER = 'rgba(148,163,184,0.24)';
const STREAM_PRIMARY_LEGEND_COLOR = '#2563eb';
const STREAM_MEAN_LEGEND_COLOR = '#94a3b8';
const STREAM_MEDIAN_LEGEND_COLOR = '#14b8a6';

const flash = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(94, 234, 212, 0.7); transform: scale(1); }
  50% { box-shadow: 0 0 0 6px rgba(94, 234, 212, 0); transform: scale(1.03); }
  100% { box-shadow: 0 0 0 0 rgba(94, 234, 212, 0); transform: scale(1); }
`;

const textFlash = keyframes`
  0% { color: #0f172a; text-shadow: none; }
  50% { color: #0d9488; text-shadow: 0 0 12px rgba(20, 184, 166, 0.42); }
  100% { color: #0f172a; text-shadow: none; }
`;

function extractAlgorithmNames(label: string): string[] {
  if (!label) return [];

  let normalized = label;
  normalized = normalized.replace(/^Complete Run:[^-]+-\s*/i, '');
  normalized = normalized.replace(/^Run\s+\d+\/\d+:\s*/i, '');
  if (normalized.includes(':')) {
    normalized = normalized.substring(normalized.lastIndexOf(':') + 1);
  }

  return normalized
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
}

const toGradientId = (name: string) => `grad-${name.replace(/[^a-z0-9]/gi, '').toLowerCase() || 'alg'}`;

const formatMetricValue = (value?: number, digits: number = 4) =>
  Number.isFinite(value) ? Number(value).toFixed(digits) : 'Pending';

const formatMetricWithUnit = (value: number | undefined, unit: string, digits: number) =>
  Number.isFinite(value) ? `${Number(value).toFixed(digits)} ${unit}` : 'Pending';

function getAlgorithmColor(name: string, index: number): string {
  const key = NAME_TO_KEY[name];
  return (key && RESULT_ALGORITHM_COLORS[key]) || FALLBACK_COLORS[index % FALLBACK_COLORS.length];
}

function getSparklineBars(points: ChartDataPoint[], maxBars: number = 12): number[] {
  const values = points
    .slice(-maxBars)
    .map((point) => point.fitness)
    .filter(Number.isFinite);

  if (values.length === 0) {
    return [];
  }

  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);
  const range = maxValue - minValue;

  return values.map((value) => {
    if (range === 0) {
      return 46;
    }

    return 20 + ((value - minValue) / range) * 80;
  });
}

interface ConvergenceTooltipProps {
  active?: boolean;
  label?: number | string;
  payload?: Array<{
    name?: unknown;
    value?: unknown;
    color?: string;
    dataKey?: unknown;
  }>;
  liveConvergence?: Record<string, ChartDataPoint[]>;
  algorithmNames?: string[];
}

interface ConvergenceSnapshotRow {
  name: string;
  value: number;
  color: string;
  isExact: boolean;
  sourceIteration: number;
}

type AlgorithmStreamMetricKey = 'fitness' | 'aggregatedQos' | 'energy' | 'cost' | 'responseTime' | 'executionTime';
type AlgorithmStreamStatisticKey =
  | 'fitnessMean'
  | 'fitnessMedian'
  | 'aggregatedQosMean'
  | 'aggregatedQosMedian'
  | 'energyMean'
  | 'energyMedian'
  | 'costMean'
  | 'costMedian'
  | 'responseTimeMean'
  | 'responseTimeMedian'
  | 'executionTimeMean'
  | 'executionTimeMedian';
type AlgorithmStreamValueKind = 'value' | 'mean' | 'median';
type AlgorithmStreamComparisonState = 'best' | 'worst' | 'neutral';

interface AlgorithmStreamDataRow {
  name: string;
  color: string;
  status: 'Running' | 'Complete' | 'Idle';
  fitness?: number;
  fitnessMean?: number;
  fitnessMedian?: number;
  aggregatedQos?: number;
  aggregatedQosMean?: number;
  aggregatedQosMedian?: number;
  energy?: number;
  energyMean?: number;
  energyMedian?: number;
  cost?: number;
  costMean?: number;
  costMedian?: number;
  responseTime?: number;
  responseTimeMean?: number;
  responseTimeMedian?: number;
  executionTime?: number;
  executionTimeMean?: number;
  executionTimeMedian?: number;
  hasStatistics: boolean;
  sampleCount: number;
}

const METRIC_STAT_FIELDS: Record<AlgorithmStreamMetricKey, { mean: AlgorithmStreamStatisticKey; median: AlgorithmStreamStatisticKey }> = {
  fitness: { mean: 'fitnessMean', median: 'fitnessMedian' },
  aggregatedQos: { mean: 'aggregatedQosMean', median: 'aggregatedQosMedian' },
  energy: { mean: 'energyMean', median: 'energyMedian' },
  cost: { mean: 'costMean', median: 'costMedian' },
  responseTime: { mean: 'responseTimeMean', median: 'responseTimeMedian' },
  executionTime: { mean: 'executionTimeMean', median: 'executionTimeMedian' },
};

const TABLE_METRIC_KEYS: AlgorithmStreamMetricKey[] = ['fitness', 'aggregatedQos', 'energy', 'cost', 'responseTime', 'executionTime'];

const METRIC_DIRECTIONS: Record<AlgorithmStreamMetricKey, 'higher' | 'lower'> = {
  fitness: 'higher',
  aggregatedQos: 'higher',
  energy: 'lower',
  cost: 'lower',
  responseTime: 'lower',
  executionTime: 'lower',
};

function getConvergenceSnapshotRows(
  hoveredIteration: number,
  liveConvergence: Record<string, ChartDataPoint[]>,
  algorithmNames: string[],
  payload?: Array<{
    name?: unknown;
    value?: unknown;
    color?: string;
    dataKey?: unknown;
  }>
): ConvergenceSnapshotRow[] {
  const payloadColorMap = new Map(
    (payload ?? []).map((entry) => [
      String(entry.name || entry.dataKey || ''),
      entry.color,
    ])
  );

  return algorithmNames
    .map((algorithmName) => {
      const points = liveConvergence[algorithmName] ?? [];
      if (points.length === 0) {
        return null;
      }

      let bestPoint: ChartDataPoint | null = null;
      let bestDistance = Number.POSITIVE_INFINITY;

      for (const point of points) {
        const distance = Math.abs(point.iteration - hoveredIteration);
        if (distance < bestDistance) {
          bestPoint = point;
          bestDistance = distance;
        }
      }

      if (!bestPoint || !Number.isFinite(bestPoint.fitness)) {
        return null;
      }

      const key = NAME_TO_KEY[algorithmName];

      return {
        name: algorithmName,
        value: bestPoint.fitness,
        color:
          payloadColorMap.get(algorithmName) ||
          (key ? RESULT_ALGORITHM_COLORS[key] : undefined) ||
          FALLBACK_COLORS[0],
        isExact: bestPoint.iteration === hoveredIteration,
        sourceIteration: bestPoint.iteration,
      };
    })
    .filter((entry): entry is ConvergenceSnapshotRow => entry !== null)
    .sort((a, b) => b.value - a.value);
}

const ConvergenceTooltip: React.FC<ConvergenceTooltipProps> = ({
  active,
  label,
  payload,
  liveConvergence = {},
  algorithmNames = [],
}) => {
  if (!active || label === undefined || label === null) {
    return null;
  }

  const hoveredIteration = Number(label);
  const rows = getConvergenceSnapshotRows(
    hoveredIteration,
    liveConvergence,
    algorithmNames,
    payload
  );

  if (rows.length === 0) {
    return null;
  }

  const values = rows.map((entry) => Number(entry.value));
  const maxValue = values.reduce((currentMax, value) => Math.max(currentMax, value), Number.NEGATIVE_INFINITY);
  const minValue = values.reduce((currentMin, value) => Math.min(currentMin, value), Number.POSITIVE_INFINITY);
  const valueRange = maxValue - minValue;

  return (
    <Box
      sx={{
        minWidth: 200,
        p: 1.25,
        borderRadius: 2,
        background: 'linear-gradient(180deg, rgba(255,255,255,0.98), rgba(248,250,252,0.96))',
        border: `1px solid ${PANEL_BORDER}`,
        boxShadow: '0 16px 32px rgba(15,23,42,0.16)',
      }}
    >
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          mb: 0.8,
          color: TEXT_SECONDARY,
          fontWeight: 800,
          textTransform: 'uppercase',
          letterSpacing: 0.5,
        }}
      >
        Iteration {label}
      </Typography>
      <Stack spacing={0.6}>
        {rows.map((entry) => {
          const color = entry.color || '#64748b';
          const name = entry.name || 'Series';
          const value = Number(entry.value);
          const normalizedWidth = valueRange > 0
            ? 18 + ((value - minValue) / valueRange) * 82
            : 100;
          return (
            <Box
              key={name}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.8,
                p: 0.75,
                borderRadius: 1.5,
                backgroundColor: `${color}14`,
                border: `1px solid ${color}33`,
              }}
            >
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  backgroundColor: color,
                  boxShadow: `0 0 0 3px ${color}22`,
                  flexShrink: 0,
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  minWidth: 64,
                  color: TEXT_PRIMARY,
                  fontWeight: 700,
                  opacity: entry.isExact ? 1 : 0.78,
                }}
              >
                {name}
              </Typography>
              <Box sx={{ flexGrow: 1, minWidth: 88 }}>
                <Box
                  sx={{
                    width: '100%',
                    height: 5,
                    borderRadius: 999,
                    overflow: 'hidden',
                    backgroundColor: `${color}20`,
                  }}
                >
                  <Box
                    sx={{
                      width: `${normalizedWidth}%`,
                      height: '100%',
                      borderRadius: 999,
                      backgroundColor: color,
                    }}
                  />
                </Box>
              </Box>
              <Typography
                variant="caption"
                sx={{
                  minWidth: 54,
                  textAlign: 'right',
                  color: color,
                  fontFamily: 'monospace',
                  fontWeight: 800,
                  opacity: entry.isExact ? 1 : 0.78,
                }}
              >
                {value.toFixed(4)}
              </Typography>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
};

const SimulationPanel: React.FC = () => {
  const [isConvergenceMaximized, setIsConvergenceMaximized] = useState(false);
  const [selectedIteration, setSelectedIteration] = useState<number | null>(null);
  const [runningAlgorithmModalOpen, setRunningAlgorithmModalOpen] = useState(false);
  const [runningAlgorithmPromptAcknowledged, setRunningAlgorithmPromptAcknowledged] = useState(false);
  const {
    isRunning,
    progress,
    currentAlgorithm,
    currentIteration,
    totalIterations,
    currentRun,
    totalRuns,
    completedAlgorithms,
    liveConvergence,
    convergenceData,
    results,
  } = useOptimization();

  const activeAlgorithms = useMemo(() => new Set(extractAlgorithmNames(currentAlgorithm)), [currentAlgorithm]);

  const openRunningAlgorithmModal = () => {
    setRunningAlgorithmPromptAcknowledged(true);
    setRunningAlgorithmModalOpen(true);
  };

  const finalConvergenceByAlgorithm = useMemo(() => {
    const entries: Record<string, ChartDataPoint[]> = {};

    if (!results || !convergenceData) {
      return entries;
    }

    getResultAlgorithmKeysForResults(results).forEach((key) => {
      const points = convergenceData[key] ?? [];
      if (points.length > 0) {
        entries[RESULT_ALGORITHM_NAMES[key]] = points;
      }
    });

    return entries;
  }, [convergenceData, results]);

  const displayedConvergence = useMemo(() => {
    const hasFinalConvergence = Object.keys(finalConvergenceByAlgorithm).length > 0;
    return !isRunning && hasFinalConvergence ? finalConvergenceByAlgorithm : liveConvergence;
  }, [finalConvergenceByAlgorithm, isRunning, liveConvergence]);

  const algorithmList = useMemo(() => {
    const list = new Set<string>();
    Object.keys(displayedConvergence || {}).forEach((alg) => list.add(alg));
    completedAlgorithms.forEach((alg) => list.add(alg));
    activeAlgorithms.forEach((alg) => list.add(alg));
    return Array.from(list);
  }, [activeAlgorithms, completedAlgorithms, displayedConvergence]);

  const mergedData = useMemo(() => {
    const iterations = new Set<number>();
    const fitnessByAlgorithm = new Map<string, Map<number, number>>();

    Object.entries(displayedConvergence).forEach(([alg, points]) => {
      const series = new Map<number, number>();
      (points || []).forEach(({ iteration, fitness }) => {
        if (!Number.isFinite(iteration)) {
          return;
        }
        iterations.add(iteration);
        series.set(iteration, fitness);
      });
      fitnessByAlgorithm.set(alg, series);
    });

    return Array.from(iterations)
      .sort((a, b) => a - b)
      .map((iteration) => {
      const row: Record<string, number> & { iteration: number } = { iteration };
      fitnessByAlgorithm.forEach((series, alg) => {
        const fitness = series.get(iteration);
        if (fitness !== undefined) {
          row[alg] = fitness;
        }
      });
      return row;
    });
  }, [displayedConvergence]);

  const latestPoints = useMemo(() => {
    const entries: Array<{
      alg: string;
      iteration: number;
      value: number;
      aggregatedQos?: number;
      energy?: number;
      cost?: number;
      responseTime?: number;
      executionTime?: number;
    }> = [];
    Object.entries(displayedConvergence).forEach(([alg, points]) => {
      if (!points || points.length === 0) return;
      const last = points[points.length - 1];
      entries.push({
        alg,
        iteration: last.iteration,
        value: last.fitness,
        aggregatedQos: last.aggregatedQos,
        energy: last.energy,
        cost: last.cost,
        responseTime: last.responseTime,
        executionTime: last.executionTime,
      });
    });
    return entries;
  }, [displayedConvergence]);

  const latestPointByAlgorithm = useMemo(() => {
    return new Map(latestPoints.map((point) => [point.alg, point]));
  }, [latestPoints]);

  const runningAlgorithmDetails = useMemo(() => {
    const activeAlgorithmList = algorithmList.filter((alg) => activeAlgorithms.has(alg));
    const isQosOnlyFitness = results?.configuration?.fitnessMode === 'qos-only';

    return activeAlgorithmList.map((alg, index) => {
      const key = NAME_TO_KEY[alg];
      const algorithmResult = key ? results?.[key] : undefined;
      const history = displayedConvergence[alg] ?? [];
      const latestPoint = latestPointByAlgorithm.get(alg);
      const previousPoint = history.length > 1 ? history[history.length - 2] : null;
      const isActive = activeAlgorithms.has(alg) && isRunning;
      const latestFitness = latestPoint?.value ?? algorithmResult?.fitness;
      const fitnessDelta =
        Number.isFinite(latestFitness) && Number.isFinite(previousPoint?.fitness)
          ? Number(latestFitness) - Number(previousPoint?.fitness)
          : undefined;
      const latestAggregatedQos =
        latestPoint?.aggregatedQos ??
        algorithmResult?.aggregatedQos ??
        (isQosOnlyFitness ? latestFitness : undefined);
      const availableMetricCount = [
        latestFitness,
        latestPoint?.energy ?? algorithmResult?.energy,
        latestPoint?.cost ?? algorithmResult?.cost,
        latestPoint?.responseTime ?? algorithmResult?.responseTime,
        latestPoint?.executionTime ?? algorithmResult?.executionTime,
        latestAggregatedQos,
      ].filter(Number.isFinite).length;

      return {
        name: alg,
        color: getAlgorithmColor(alg, index),
        status: isActive ? 'Running' : 'Pending',
        latestFitness,
        fitnessDelta,
        aggregatedQos: latestAggregatedQos,
        energy: latestPoint?.energy ?? algorithmResult?.energy,
        cost: latestPoint?.cost ?? algorithmResult?.cost,
        responseTime: latestPoint?.responseTime ?? algorithmResult?.responseTime,
        metricReadiness: (availableMetricCount / 6) * 100,
        sampleCount: history.length,
        sparklineBars: getSparklineBars(history),
      };
    });
  }, [activeAlgorithms, algorithmList, displayedConvergence, isRunning, latestPointByAlgorithm, results]);

  const algorithmStreamRows = useMemo<AlgorithmStreamDataRow[]>(() => {
    const names = new Set<string>();
    const isQosOnlyFitness = results?.configuration?.fitnessMode === 'qos-only';
    algorithmList.forEach((name) => names.add(name));

    if (results) {
      getResultAlgorithmKeysForResults(results).forEach((key) => {
        names.add(RESULT_ALGORITHM_NAMES[key]);
      });
    }

    return Array.from(names).map((name, index) => {
      const key = NAME_TO_KEY[name];
      const algorithmResult = key ? results?.[key] : undefined;
      const algorithmStats = key ? results?.statistics?.[key] : undefined;
      const latestPoint = latestPointByAlgorithm.get(name);
      const history = displayedConvergence[name] ?? [];
      const isActive = activeAlgorithms.has(name) && isRunning;
      const isComplete = completedAlgorithms.has(name) || Boolean(algorithmResult);
      const fitnessValue = latestPoint?.value ?? algorithmResult?.fitness;
      const aggregatedQosValue =
        latestPoint?.aggregatedQos ??
        algorithmResult?.aggregatedQos ??
        (isQosOnlyFitness ? fitnessValue : undefined);
      const aggregatedQosStats = algorithmStats?.aggregatedQos ?? algorithmStats?.fitness;

      return {
        name,
        color: key ? RESULT_ALGORITHM_COLORS[key] : getAlgorithmColor(name, index),
        status: isActive ? 'Running' : isComplete ? 'Complete' : 'Idle',
        fitness: fitnessValue,
        fitnessMean: algorithmStats?.fitness.mean,
        fitnessMedian: algorithmStats?.fitness.median,
        aggregatedQos: aggregatedQosValue,
        aggregatedQosMean: aggregatedQosStats?.mean,
        aggregatedQosMedian: aggregatedQosStats?.median,
        energy: latestPoint?.energy ?? algorithmResult?.energy,
        energyMean: algorithmStats?.energy.mean,
        energyMedian: algorithmStats?.energy.median,
        cost: latestPoint?.cost ?? algorithmResult?.cost,
        costMean: algorithmStats?.cost.mean,
        costMedian: algorithmStats?.cost.median,
        responseTime: latestPoint?.responseTime ?? algorithmResult?.responseTime,
        responseTimeMean: algorithmStats?.responseTime.mean,
        responseTimeMedian: algorithmStats?.responseTime.median,
        executionTime: !isRunning && algorithmResult
          ? algorithmResult.executionTime
          : latestPoint?.executionTime ?? algorithmResult?.executionTime,
        executionTimeMean: algorithmStats?.executionTime.mean,
        executionTimeMedian: algorithmStats?.executionTime.median,
        hasStatistics: Boolean(algorithmStats),
        sampleCount: history.length,
      };
    });
  }, [activeAlgorithms, algorithmList, completedAlgorithms, displayedConvergence, isRunning, latestPointByAlgorithm, results]);

  const algorithmStreamConvergenceEntries = useMemo<Array<[string, ChartDataPoint[]]>>(() => {
    const entries = new Map<string, ChartDataPoint[]>();

    Object.entries(displayedConvergence).forEach(([name, points]) => {
      if (points.length > 0) {
        entries.set(name, points);
      }
    });

    if (results) {
      getResultAlgorithmKeysForResults(results).forEach((key) => {
        const name = RESULT_ALGORITHM_NAMES[key];
        const points = convergenceData?.[key] ?? [];
        if (!entries.has(name) && points.length > 0) {
          entries.set(name, points);
        }
      });
    }

    return Array.from(entries.entries());
  }, [convergenceData, displayedConvergence, results]);

  const algorithmStreamConvergenceRows = useMemo(() => {
    const iterations = new Set<number>();
    const valuesByAlgorithm = new Map<string, Map<number, number>>();

    algorithmStreamConvergenceEntries.forEach(([name, points]) => {
      const series = new Map<number, number>();
      points.forEach((point) => {
        if (!Number.isFinite(point.iteration) || !Number.isFinite(point.fitness)) {
          return;
        }

        iterations.add(point.iteration);
        series.set(point.iteration, point.fitness);
      });
      valuesByAlgorithm.set(name, series);
    });

    return Array.from(iterations)
      .sort((a, b) => a - b)
      .map((iteration) => ({
        iteration,
        ...Object.fromEntries(
          algorithmStreamConvergenceEntries.map(([name]) => [
            name,
            valuesByAlgorithm.get(name)?.get(iteration) ?? null,
          ])
        ),
      }));
  }, [algorithmStreamConvergenceEntries]);

  const hasAlgorithmStreamModalData =
    algorithmStreamRows.length > 0 || algorithmStreamConvergenceRows.length > 0;
  const hasAlgorithmStatistics = algorithmStreamRows.some((row) => row.hasStatistics);
  const allAlgorithmRunsComplete =
    !isRunning &&
    algorithmStreamRows.length > 0 &&
    algorithmStreamRows.every((row) => row.status === 'Complete');
  const shouldFlashRunningAlgorithmPrompt =
    allAlgorithmRunsComplete && !runningAlgorithmPromptAcknowledged && !runningAlgorithmModalOpen;
  const primaryMetricLabel = isRunning ? 'Live' : hasAlgorithmStatistics ? 'Last run' : 'Final';

  useEffect(() => {
    if (isRunning) {
      setRunningAlgorithmPromptAcknowledged(false);
      return;
    }

    if (allAlgorithmRunsComplete) {
      setRunningAlgorithmPromptAcknowledged(false);
    }
  }, [allAlgorithmRunsComplete, isRunning, results?.timestamp]);

  const algorithmStreamMetricExtremes = useMemo(() => {
    const extremes = new Map<string, { best: number; worst: number }>();
    const valueKinds: Array<{
      kind: AlgorithmStreamValueKind;
      getField: (metricKey: AlgorithmStreamMetricKey) => AlgorithmStreamMetricKey | AlgorithmStreamStatisticKey;
    }> = [
      { kind: 'value', getField: (metricKey) => metricKey },
      { kind: 'mean', getField: (metricKey) => METRIC_STAT_FIELDS[metricKey].mean },
      { kind: 'median', getField: (metricKey) => METRIC_STAT_FIELDS[metricKey].median },
    ];

    TABLE_METRIC_KEYS.forEach((metricKey) => {
      valueKinds.forEach(({ kind, getField }) => {
        const field = getField(metricKey);
        const values = algorithmStreamRows
          .map((row) => row[field as keyof AlgorithmStreamDataRow])
          .filter((value): value is number => typeof value === 'number' && Number.isFinite(value));

        if (values.length < 2) {
          return;
        }

        const min = Math.min(...values);
        const max = Math.max(...values);

        if (Math.abs(max - min) < 1e-9) {
          return;
        }

        extremes.set(
          `${metricKey}:${kind}`,
          METRIC_DIRECTIONS[metricKey] === 'higher'
            ? { best: max, worst: min }
            : { best: min, worst: max }
        );
      });
    });

    return extremes;
  }, [algorithmStreamRows]);

  const getTableMetricComparisonState = (
    metricKey: AlgorithmStreamMetricKey,
    valueKind: AlgorithmStreamValueKind,
    value: number | undefined
  ): AlgorithmStreamComparisonState => {
    if (typeof value !== 'number' || !Number.isFinite(value)) {
      return 'neutral';
    }

    const extremes = algorithmStreamMetricExtremes.get(`${metricKey}:${valueKind}`);
    if (!extremes) {
      return 'neutral';
    }

    if (Math.abs(value - extremes.best) < 1e-9) {
      return 'best';
    }

    if (Math.abs(value - extremes.worst) < 1e-9) {
      return 'worst';
    }

    return 'neutral';
  };

  const executionTimeEntries = useMemo(() => {
    const names = new Set<string>();
    algorithmList.forEach((name) => names.add(name));
    if (results) {
      getResultAlgorithmKeysForResults(results).forEach((key) => {
        names.add(RESULT_ALGORITHM_NAMES[key]);
      });
    }

    return Array.from(names)
      .map((name) => {
        const key = NAME_TO_KEY[name];
        const algorithmResult = key ? results?.[key] : undefined;
        const latestPoint = latestPointByAlgorithm.get(name);
        const executionTime = !isRunning && algorithmResult
          ? algorithmResult.executionTime
          : latestPoint?.executionTime ?? algorithmResult?.executionTime;

        if (!Number.isFinite(executionTime)) {
          return null;
        }

        return {
          key: key ?? name,
          name,
          executionTime: executionTime as number,
          color: key ? RESULT_ALGORITHM_COLORS[key] : getAlgorithmColor(name, 0),
          isCompleted: completedAlgorithms.has(name),
          isActive: activeAlgorithms.has(name) && isRunning,
        };
      })
      .filter((entry): entry is NonNullable<typeof entry> => entry !== null)
      .sort((a, b) => a.executionTime - b.executionTime);
  }, [activeAlgorithms, algorithmList, completedAlgorithms, isRunning, latestPointByAlgorithm, results]);

  const maxExecutionTime = useMemo(
    () => executionTimeEntries.reduce((maxValue, entry) => Math.max(maxValue, entry.executionTime), 0),
    [executionTimeEntries]
  );
  const selectedSnapshotRows = useMemo(
    () =>
      selectedIteration === null
        ? []
        : getConvergenceSnapshotRows(selectedIteration, displayedConvergence, algorithmList),
    [algorithmList, displayedConvergence, selectedIteration]
  );

  const hasLiveData = mergedData.length > 0;
  const convergenceStatusLabel = isRunning ? 'Streaming' : hasLiveData ? 'Complete' : 'Waiting';
  const iterationPercent = totalIterations > 0 ? Math.min(100, (currentIteration / totalIterations) * 100) : 0;

  const getColor = (name: string, index: number) => {
    const key = NAME_TO_KEY[name];
    return (key && RESULT_ALGORITHM_COLORS[key]) || FALLBACK_COLORS[index % FALLBACK_COLORS.length];
  };
  const bestSelectedSnapshot = selectedSnapshotRows[0];
  const worstSelectedSnapshot = selectedSnapshotRows[selectedSnapshotRows.length - 1];
  const selectedSnapshotSpread =
    bestSelectedSnapshot && worstSelectedSnapshot
      ? bestSelectedSnapshot.value - worstSelectedSnapshot.value
      : 0;
  const approximateSelectedCount = selectedSnapshotRows.filter((row) => !row.isExact).length;
  const renderAlgorithmStreamMetricChart = (
    title: string,
    dataKey: AlgorithmStreamMetricKey,
    unit: string,
    lowerIsBetter: boolean = false
  ) => {
    const statFields = METRIC_STAT_FIELDS[dataKey];
    const hasMetricValues = algorithmStreamRows.some(
      (row) =>
        Number.isFinite(row[dataKey]) ||
        Number.isFinite(row[statFields.mean]) ||
        Number.isFinite(row[statFields.median])
    );
    const hasMetricStats = algorithmStreamRows.some(
      (row) => Number.isFinite(row[statFields.mean]) || Number.isFinite(row[statFields.median])
    );

    return (
      <Paper variant="outlined" sx={{ p: 1.5, minWidth: 0, height: '100%' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: TEXT_PRIMARY }}>
          {title}
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', mb: 1.25, color: TEXT_SECONDARY }}>
          {lowerIsBetter ? 'Lower values are better.' : 'Higher values are better.'}
          {hasMetricStats ? ` ${primaryMetricLabel}, median, and mean are shown.` : ''}
        </Typography>
        <Box sx={{ height: 240 }}>
          {hasMetricValues ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={algorithmStreamRows} margin={{ top: 8, right: 8, left: 0, bottom: 28 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
                <XAxis dataKey="name" angle={-20} textAnchor="end" interval={0} height={54} tick={{ fontSize: 11, fill: AXIS_STROKE }} />
                <YAxis stroke={AXIS_STROKE} tick={{ fontSize: 11, fill: AXIS_STROKE }} />
                <RechartsTooltip
                  contentStyle={{ backgroundColor: '#ffffff', border: `1px solid ${PANEL_BORDER}`, color: TEXT_PRIMARY }}
                  labelStyle={{ color: TEXT_PRIMARY, fontWeight: 700 }}
                  formatter={(value: any) => (
                    Number.isFinite(value)
                      ? `${Number(value).toFixed(dataKey === 'responseTime' || dataKey === 'executionTime' ? 2 : 4)}${unit}`
                      : 'Pending'
                  )}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar
                  dataKey={dataKey}
                  name={primaryMetricLabel}
                  fill={STREAM_PRIMARY_LEGEND_COLOR}
                  radius={[6, 6, 0, 0]}
                >
                  {algorithmStreamRows.map((entry) => (
                    <Cell key={`${title}-${entry.name}-primary`} fill={entry.color} />
                  ))}
                </Bar>
                {hasMetricStats && (
                  <Bar
                    dataKey={statFields.median}
                    name="Median"
                    fill={STREAM_MEDIAN_LEGEND_COLOR}
                    radius={[6, 6, 0, 0]}
                  >
                    {algorithmStreamRows.map((entry) => (
                      <Cell key={`${title}-${entry.name}-median`} fill={entry.color} fillOpacity={0.72} />
                    ))}
                  </Bar>
                )}
                {hasMetricStats && (
                  <Bar
                    dataKey={statFields.mean}
                    name="Mean"
                    fill={STREAM_MEAN_LEGEND_COLOR}
                    radius={[6, 6, 0, 0]}
                  >
                    {algorithmStreamRows.map((entry) => (
                      <Cell key={`${title}-${entry.name}-mean`} fill={entry.color} fillOpacity={0.38} />
                    ))}
                  </Bar>
                )}
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant="body2" sx={{ color: TEXT_SECONDARY }}>
                Waiting for {title.toLowerCase()} data.
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>
    );
  };

  const renderAlgorithmMetricTableCell = (
    row: AlgorithmStreamDataRow,
    metricKey: AlgorithmStreamMetricKey,
    unit: string,
    digits: number
  ) => {
    const statFields = METRIC_STAT_FIELDS[metricKey];
    const value = row[metricKey] as number | undefined;
    const mean = row[statFields.mean] as number | undefined;
    const median = row[statFields.median] as number | undefined;
    const hasStats = Number.isFinite(mean) || Number.isFinite(median);
    const formatter = unit
      ? (metricValue: number | undefined) => formatMetricWithUnit(metricValue, unit, digits)
      : (metricValue: number | undefined) => formatMetricValue(metricValue, digits);
    const renderValueLine = (
      label: string | null,
      metricValue: number | undefined,
      valueKind: AlgorithmStreamValueKind,
      emphasized: boolean = false
    ) => {
      const comparisonState = getTableMetricComparisonState(metricKey, valueKind, metricValue);
      const isBest = comparisonState === 'best';
      const isWorst = comparisonState === 'worst';

      return (
        <Typography
          variant={emphasized ? 'body2' : 'caption'}
          component="div"
          sx={{
            display: 'inline-flex',
            width: 'fit-content',
            alignItems: 'center',
            px: isBest || isWorst ? 0.6 : 0,
            py: isBest || isWorst ? 0.15 : 0,
            borderRadius: 0.75,
            color: isBest ? '#166534' : isWorst ? '#991b1b' : emphasized ? TEXT_PRIMARY : TEXT_SECONDARY,
            backgroundColor: isBest
              ? 'rgba(34,197,94,0.14)'
              : isWorst
                ? 'rgba(239,68,68,0.13)'
                : 'transparent',
            border: isBest
              ? '1px solid rgba(22,163,74,0.28)'
              : isWorst
                ? '1px solid rgba(220,38,38,0.28)'
                : '1px solid transparent',
            fontFamily: 'monospace',
            fontWeight: emphasized || isBest || isWorst ? 800 : 500,
          }}
        >
          {label ? `${label}: ` : ''}
          {formatter(metricValue)}
        </Typography>
      );
    };

    return (
      <TableCell>
        <Stack spacing={0.15}>
          {renderValueLine(null, value, 'value', true)}
          {hasStats && (
            <>
              {renderValueLine('Mean', mean, 'mean')}
              {renderValueLine('Median', median, 'median')}
            </>
          )}
        </Stack>
      </TableCell>
    );
  };

  return (
    <Box sx={{ color: TEXT_PRIMARY }}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={isConvergenceMaximized ? 12 : 4}>
          <Stack spacing={2}>
            <Paper
              elevation={4}
              sx={{
                p: 2.5,
                background: PANEL_BG,
                border: `1px solid ${PANEL_BORDER}`,
                color: TEXT_PRIMARY,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Stack spacing={0.5}>
                  <Typography variant="body2" sx={{ color: TEXT_SECONDARY }}>
                    Overall Progress
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {Math.round(progress)}%
                  </Typography>
                </Stack>
                <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                  <CircularProgress
                    variant="determinate"
                    value={Math.max(progress, 6)}
                    size={82}
                    thickness={5}
                    sx={{ color: '#5eead4' }}
                  />
                  <Box
                    sx={{
                      top: 0,
                      left: 0,
                      bottom: 0,
                      right: 0,
                      position: 'absolute',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography variant="subtitle2" component="div" sx={{ color: TEXT_PRIMARY }}>
                      {currentRun > 0 ? `${currentRun}/${totalRuns}` : 'Run'}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {totalRuns > 1 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" sx={{ color: TEXT_SECONDARY }}>
                    Statistical Run
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={(currentRun / totalRuns) * 100}
                    sx={{
                      height: 10,
                      borderRadius: 5,
                      backgroundColor: SOFT_FILL,
                      '& .MuiLinearProgress-bar': { backgroundColor: '#93c5fd' },
                    }}
                  />
                </Box>
              )}

              <Box>
                <Typography variant="caption" sx={{ color: TEXT_SECONDARY }}>
                  Iteration Progress
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <SpeedIcon fontSize="small" sx={{ color: '#5eead4' }} />
                  <Typography variant="body2" fontWeight={600}>
                    {currentIteration} / {totalIterations || '—'}
                  </Typography>
                  <Typography variant="body2" sx={{ ml: 'auto', color: TEXT_SECONDARY }}>
                    {Math.round(iterationPercent)}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={iterationPercent}
                  sx={{
                    mt: 1,
                    height: 10,
                    borderRadius: 5,
                    backgroundColor: SOFT_FILL,
                    '& .MuiLinearProgress-bar': { backgroundColor: '#5eead4' },
                  }}
                />
              </Box>
            </Paper>

            <Paper
              elevation={3}
              sx={{
                p: 0,
                overflow: 'hidden',
                background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
                border: `1px solid ${PANEL_BORDER}`,
                color: TEXT_PRIMARY,
              }}
            >
              <Stack spacing={0}>
                <Box
                  sx={{
                    p: 2,
                    borderBottom: `1px solid ${PANEL_BORDER}`,
                    background:
                      'linear-gradient(135deg, rgba(94,234,212,0.12), rgba(147,197,253,0.08) 48%, rgba(255,255,255,0.86))',
                  }}
                >
                  <Tooltip title="Open running algorithm charts">
                    <Box
                      role="button"
                      tabIndex={0}
                      onClick={openRunningAlgorithmModal}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          openRunningAlgorithmModal();
                        }
                      }}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                        cursor: 'pointer',
                        borderRadius: 1,
                        outline: 'none',
                        '&:focus-visible': {
                          boxShadow: `0 0 0 3px ${SOFT_BORDER}`,
                        },
                      }}
                    >
                      <Box>
                        <Typography
                          variant="subtitle1"
                          fontWeight={800}
                          sx={{
                            animation: shouldFlashRunningAlgorithmPrompt
                              ? `${textFlash} 1.1s ease-in-out infinite`
                              : 'none',
                          }}
                        >
                          Running Algorithm
                        </Typography>
                      </Box>
                      <Chip
                        label={isRunning ? 'Live' : allAlgorithmRunsComplete ? 'View charts' : 'Idle'}
                        size="small"
                        sx={{
                          color: isRunning || allAlgorithmRunsComplete ? '#0f766e' : TEXT_SECONDARY,
                          backgroundColor:
                            isRunning || allAlgorithmRunsComplete
                              ? 'rgba(45,212,191,0.15)'
                              : 'rgba(148,163,184,0.12)',
                          border: `1px solid ${isRunning || allAlgorithmRunsComplete ? 'rgba(13,148,136,0.24)' : SOFT_BORDER}`,
                          fontWeight: 800,
                          textTransform: 'uppercase',
                          letterSpacing: 0,
                          animation: shouldFlashRunningAlgorithmPrompt
                            ? `${flash} 1.1s ease-in-out infinite`
                            : 'none',
                        }}
                      />
                    </Box>
                  </Tooltip>
                </Box>

                {runningAlgorithmDetails.length > 0 ? (
                  <Stack spacing={1.25} sx={{ p: 1.5 }}>
                    {runningAlgorithmDetails.map((detail) => {
                      const trendLabel =
                        detail.fitnessDelta === undefined
                          ? 'Collecting signal'
                          : detail.fitnessDelta > 0
                            ? `Improved +${detail.fitnessDelta.toFixed(4)}`
                            : detail.fitnessDelta < 0
                              ? `Changed ${detail.fitnessDelta.toFixed(4)}`
                              : 'Stable';
                      const trendColor =
                        detail.fitnessDelta === undefined
                          ? TEXT_SECONDARY
                          : detail.fitnessDelta >= 0
                            ? '#047857'
                            : '#b45309';
                      const metricTiles = [
                        {
                          label: 'Fitness',
                          value: formatMetricValue(detail.latestFitness),
                          accent: detail.color,
                          helper: trendLabel,
                        },
                        {
                          label: 'Energy',
                          value: formatMetricWithUnit(detail.energy, 'J', 4),
                          accent: '#0f766e',
                          helper: 'Lower is better',
                        },
                        {
                          label: 'Cost',
                          value: formatMetricValue(detail.cost),
                          accent: '#7c3aed',
                          helper: 'Execution price',
                        },
                        {
                          label: 'Response',
                          value: formatMetricWithUnit(detail.responseTime, 'ms', 2),
                          accent: '#0369a1',
                          helper: 'Latency signal',
                        },
                      ];

                      return (
                      <Box
                        key={detail.name}
                        sx={{
                          p: 1.35,
                          borderRadius: 2,
                          backgroundColor: '#ffffff',
                          border: `1px solid ${detail.color}30`,
                          boxShadow: `inset 4px 0 0 ${detail.color}, 0 10px 22px rgba(15,23,42,0.05)`,
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Box
                            sx={{
                              width: 28,
                              height: 28,
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: detail.color,
                              boxShadow: `0 0 0 4px ${detail.color}20`,
                              flexShrink: 0,
                              animation: detail.status === 'Running' ? `${flash} 1.8s ease-in-out infinite` : 'none',
                            }}
                          >
                            <AutoAwesomeIcon sx={{ color: '#ffffff', fontSize: 16 }} />
                          </Box>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={900} sx={{ color: TEXT_PRIMARY }}>
                              {detail.name}
                            </Typography>
                            <Typography variant="caption" sx={{ color: trendColor, fontWeight: 700 }}>
                              {trendLabel}
                            </Typography>
                          </Box>
                          {detail.status !== 'Running' && (
                            <Chip
                              label={detail.status}
                              size="small"
                              sx={{
                                ml: 'auto',
                                height: 20,
                                color: TEXT_SECONDARY,
                                backgroundColor: 'rgba(148,163,184,0.12)',
                                border: `1px solid ${SOFT_BORDER}`,
                                fontWeight: 700,
                              }}
                            />
                          )}
                        </Box>

                        <Grid container spacing={0.8}>
                          {metricTiles.map((metric) => (
                            <Grid key={metric.label} item xs={6}>
                              <Box
                                sx={{
                                  p: 0.9,
                                  minHeight: 74,
                                  borderRadius: 1.5,
                                  backgroundColor: `${metric.accent}0F`,
                                  border: `1px solid ${metric.accent}24`,
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{
                                    display: 'block',
                                    mb: 0.35,
                                    color: TEXT_SECONDARY,
                                    fontWeight: 800,
                                    textTransform: 'uppercase',
                                    letterSpacing: 0.4,
                                  }}
                                >
                                  {metric.label}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  fontWeight={900}
                                  sx={{
                                    color: metric.value === 'Pending' ? TEXT_SECONDARY : TEXT_PRIMARY,
                                    fontFamily: 'monospace',
                                    lineHeight: 1.2,
                                    overflowWrap: 'anywhere',
                                  }}
                                >
                                  {metric.value}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    display: 'block',
                                    mt: 0.45,
                                    color: metric.label === 'Fitness' ? trendColor : TEXT_SECONDARY,
                                    fontSize: '0.68rem',
                                  }}
                                >
                                  {metric.helper}
                                </Typography>
                              </Box>
                            </Grid>
                          ))}
                        </Grid>

                        <Box sx={{ mt: 1.2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.6 }}>
                            <Typography variant="caption" sx={{ color: TEXT_SECONDARY, fontWeight: 800 }}>
                              Fitness Sparkline
                            </Typography>
                            <Typography variant="caption" sx={{ ml: 'auto', color: TEXT_SECONDARY }}>
                              {detail.sampleCount} samples
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              height: 64,
                              display: 'flex',
                              alignItems: 'flex-end',
                              gap: 0.45,
                              px: 0.75,
                              py: 0.7,
                              borderRadius: 1.5,
                              backgroundColor: 'rgba(248,250,252,0.95)',
                              border: `1px solid ${PANEL_BORDER}`,
                            }}
                          >
                            {detail.sparklineBars.length > 0 ? (
                              detail.sparklineBars.map((height, index) => (
                                <Box
                                  key={`${detail.name}-spark-${index}`}
                                  sx={{
                                    flex: 1,
                                    minWidth: 3,
                                    height: `${height}%`,
                                    borderRadius: 999,
                                    backgroundColor: detail.color,
                                    opacity: 0.35 + (index + 1) / detail.sparklineBars.length * 0.55,
                                  }}
                                />
                              ))
                            ) : (
                              <Typography variant="caption" sx={{ color: TEXT_SECONDARY }}>
                                Waiting for fitness samples
                              </Typography>
                            )}
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={detail.metricReadiness}
                            sx={{
                              mt: 0.9,
                              height: 6,
                              borderRadius: 999,
                              backgroundColor: `${detail.color}16`,
                              '& .MuiLinearProgress-bar': {
                                borderRadius: 999,
                                backgroundColor: detail.color,
                              },
                            }}
                          />
                        </Box>
                      </Box>
                    );
                  })}
                  </Stack>
                ) : (
                  <Box
                    sx={{
                      m: 1.5,
                      p: 1.35,
                      borderRadius: 2,
                      backgroundColor: 'rgba(248,250,252,0.9)',
                      border: `1px solid ${PANEL_BORDER}`,
                    }}
                  >
                    <Typography variant="body2" fontWeight={700} sx={{ color: TEXT_PRIMARY }}>
                      {isRunning ? 'Preparing the next algorithm...' : 'No algorithm is running'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: TEXT_SECONDARY }}>
                      Details appear here as soon as an algorithm starts streaming iterations.
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Paper>
          </Stack>
        </Grid>

        <Grid item xs={12} md={isConvergenceMaximized ? 12 : 8}>
          <Paper
            elevation={4}
            sx={{
              p: 2.5,
              height: '100%',
              background: PANEL_BG,
              border: `1px solid ${PANEL_BORDER}`,
              color: TEXT_PRIMARY,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Box>
                <Typography variant="subtitle1" fontWeight={700}>
                  Live Convergence
                </Typography>
                <Typography variant="body2" sx={{ color: TEXT_SECONDARY }}>
                  Streaming the recorded convergence points for the active run
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Chip
                  label={convergenceStatusLabel}
                  color={isRunning ? 'success' : hasLiveData ? 'primary' : 'default'}
                  size="small"
                  sx={{
                    color: isRunning ? '#166534' : hasLiveData ? '#1d4ed8' : TEXT_SECONDARY,
                    backgroundColor: isRunning
                      ? 'rgba(34,197,94,0.12)'
                      : hasLiveData
                        ? 'rgba(37,99,235,0.12)'
                        : 'rgba(148,163,184,0.12)',
                  }}
                />
                <Tooltip title={isConvergenceMaximized ? 'Minimize live convergence' : 'Maximize live convergence'}>
                  <IconButton
                    size="small"
                    onClick={() => setIsConvergenceMaximized((prev) => !prev)}
                    aria-label={isConvergenceMaximized ? 'Minimize live convergence' : 'Maximize live convergence'}
                  >
                    {isConvergenceMaximized ? <CloseFullscreenIcon fontSize="small" /> : <OpenInFullIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              <Box sx={{ height: isConvergenceMaximized ? 440 : 320, transition: 'height 0.2s ease' }}>
                {hasLiveData ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                      data={mergedData}
                      margin={{ top: 16, right: 16, left: 0, bottom: 8 }}
                      onClick={(state: { activeLabel?: number | string } | undefined) => {
                        if (state?.activeLabel === undefined || state.activeLabel === null) {
                          return;
                        }
                        const clickedIteration = Number(state.activeLabel);
                        if (Number.isFinite(clickedIteration)) {
                          setSelectedIteration(clickedIteration);
                        }
                      }}
                    >
                      <defs>
                        {Object.keys(displayedConvergence).map((alg, idx) => (
                          <linearGradient key={alg} id={toGradientId(alg)} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={getColor(alg, idx)} stopOpacity={0.78} />
                            <stop offset="95%" stopColor={getColor(alg, idx)} stopOpacity={0.08} />
                          </linearGradient>
                        ))}
                      </defs>
                      <CartesianGrid strokeDasharray="4 4" stroke={GRID_STROKE} />
                      <XAxis dataKey="iteration" stroke={AXIS_STROKE} tick={{ fontSize: 12, fill: AXIS_STROKE }} />
                      <YAxis stroke={AXIS_STROKE} tick={{ fontSize: 12, fill: AXIS_STROKE }} domain={['auto', 'auto']} />
                      <RechartsTooltip
                        content={(tooltipProps) => (
                          <ConvergenceTooltip
                            {...tooltipProps}
                            liveConvergence={displayedConvergence}
                            algorithmNames={algorithmList}
                          />
                        )}
                      />
                      <Legend wrapperStyle={{ color: TEXT_PRIMARY, fontSize: 12 }} />
                      {Object.keys(displayedConvergence).map((alg, idx) => (
                        <Area
                          key={alg}
                          type="monotone"
                          dataKey={alg}
                          stroke={getColor(alg, idx)}
                          fill={`url(#${toGradientId(alg)})`}
                          strokeWidth={2.2}
                          dot={false}
                          connectNulls
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          isAnimationActive={false}
                        />
                      ))}
                      {latestPoints.map((p, idx) => (
                        <ReferenceDot
                          key={`${p.alg}-current`}
                          x={p.iteration}
                          y={p.value}
                          r={4.5}
                          fill="#ffffff"
                          stroke={getColor(p.alg, idx)}
                          strokeWidth={2}
                          ifOverflow="visible"
                          label={{
                            value: `${p.alg}: ${p.value.toFixed(4)}`,
                            position: 'left',
                            offset: 8,
                            fill: getColor(p.alg, idx),
                            fontSize: 11,
                          }}
                        />
                      ))}
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: TEXT_SECONDARY, flexDirection: 'column', gap: 1 }}>
                    <CircularProgress size={32} sx={{ color: '#5eead4' }} />
                    <Typography variant="body2">Waiting for the first iterations...</Typography>
                  </Box>
                )}
              </Box>
              <Typography variant="caption" sx={{ color: TEXT_SECONDARY }}>
                Hover to inspect values. Click an iteration to open an explanation modal.
              </Typography>

              <Box
                sx={{
                  p: 1.2,
                  borderRadius: 2,
                  backgroundColor: 'rgba(248,250,252,0.9)',
                  border: `1px solid ${PANEL_BORDER}`,
                }}
              >
                <Typography variant="caption" sx={{ display: 'block', fontWeight: 800, color: TEXT_PRIMARY }}>
                  Execution Time
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', mb: 0.75, color: TEXT_SECONDARY }}>
                  elapsed ms while running, final ms when complete
                </Typography>

                {executionTimeEntries.length > 0 ? (
                  <Stack spacing={0.55}>
                    {executionTimeEntries.map((entry) => (
                      <Box key={entry.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Box
                          sx={{
                            width: 7,
                            height: 7,
                            borderRadius: '50%',
                            backgroundColor: entry.color,
                            flexShrink: 0,
                            boxShadow: entry.isActive ? `0 0 0 3px ${entry.color}22` : 'none',
                          }}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            width: 52,
                            flexShrink: 0,
                            color: TEXT_PRIMARY,
                            fontWeight: entry.isCompleted ? 700 : 600,
                          }}
                        >
                          {entry.name}
                        </Typography>
                        <Box
                          sx={{
                            flexGrow: 1,
                            height: 5,
                            borderRadius: 999,
                            overflow: 'hidden',
                            backgroundColor: 'rgba(148,163,184,0.18)',
                          }}
                        >
                          <Box
                            sx={{
                              width: `${maxExecutionTime > 0 ? (entry.executionTime / maxExecutionTime) * 100 : 0}%`,
                              height: '100%',
                              borderRadius: 999,
                              backgroundColor: entry.color,
                              opacity: entry.isCompleted ? 1 : 0.8,
                            }}
                          />
                        </Box>
                        <Typography
                          variant="caption"
                          sx={{
                            minWidth: 44,
                            textAlign: 'right',
                            color: TEXT_PRIMARY,
                            fontFamily: 'monospace',
                            fontWeight: 700,
                          }}
                        >
                          {entry.executionTime.toFixed(1)}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="caption" sx={{ color: TEXT_SECONDARY }}>
                    {isRunning ? 'Waiting for the first timed progress update...' : 'No execution-time data yet.'}
                  </Typography>
                )}
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Dialog
        open={runningAlgorithmModalOpen}
        onClose={() => setRunningAlgorithmModalOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '92vh',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            pb: 1,
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              Running Algorithm Charts
            </Typography>
            <Typography variant="body2" sx={{ color: TEXT_SECONDARY }}>
              Algorithm Stream data from the active or latest optimization run.
              {hasAlgorithmStatistics ? ' Mean and median values are included for multi-run statistics.' : ''}
            </Typography>
          </Box>
          <IconButton
            onClick={() => setRunningAlgorithmModalOpen(false)}
            aria-label="Close running algorithm charts"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {hasAlgorithmStreamModalData ? (
            <Stack spacing={2.5}>
              {hasAlgorithmStatistics && (
                <Paper variant="outlined" sx={{ p: 1.5, backgroundColor: PANEL_BG_SUBTLE }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: TEXT_PRIMARY }}>
                    Multi-run statistics
                  </Typography>
                  <Typography variant="body2" sx={{ color: TEXT_SECONDARY }}>
                    Charts compare {primaryMetricLabel.toLowerCase()} values against each algorithm&apos;s mean and median across completed runs.
                  </Typography>
                </Paper>
              )}

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  {renderAlgorithmStreamMetricChart('Fitness', 'fitness', '', false)}
                </Grid>
                <Grid item xs={12} md={6}>
                  {renderAlgorithmStreamMetricChart('Aggregated QoS', 'aggregatedQos', '', false)}
                </Grid>
                <Grid item xs={12} md={6}>
                  {renderAlgorithmStreamMetricChart('Energy', 'energy', ' J', true)}
                </Grid>
                <Grid item xs={12} md={6}>
                  {renderAlgorithmStreamMetricChart('Cost', 'cost', '', true)}
                </Grid>
                <Grid item xs={12} md={6}>
                  {renderAlgorithmStreamMetricChart('Response Time', 'responseTime', ' ms', true)}
                </Grid>
                {algorithmStreamRows.some((row) => Number.isFinite(row.executionTime)) && (
                  <Grid item xs={12} md={6}>
                    {renderAlgorithmStreamMetricChart('Execution Time', 'executionTime', ' ms', true)}
                  </Grid>
                )}
              </Grid>

              <Paper variant="outlined" sx={{ p: 1.5, minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: TEXT_PRIMARY }}>
                  Fitness Convergence
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', mb: 1.25, color: TEXT_SECONDARY }}>
                  Saved iteration samples from the stream are plotted together for comparison.
                </Typography>
                <Box sx={{ height: 360 }}>
                  {algorithmStreamConvergenceRows.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={algorithmStreamConvergenceRows} margin={{ top: 12, right: 18, left: 0, bottom: 8 }}>
                        <defs>
                          {algorithmStreamConvergenceEntries.map(([name], index) => {
                            const rowColor =
                              algorithmStreamRows.find((row) => row.name === name)?.color ??
                              FALLBACK_COLORS[index % FALLBACK_COLORS.length];
                            return (
                              <linearGradient key={name} id={`stream-modal-${toGradientId(name)}`} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor={rowColor} stopOpacity={0.55} />
                                <stop offset="95%" stopColor={rowColor} stopOpacity={0.06} />
                              </linearGradient>
                            );
                          })}
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
                        <XAxis dataKey="iteration" stroke={AXIS_STROKE} tick={{ fontSize: 12, fill: AXIS_STROKE }} />
                        <YAxis stroke={AXIS_STROKE} tick={{ fontSize: 12, fill: AXIS_STROKE }} domain={['auto', 'auto']} />
                        <RechartsTooltip
                          contentStyle={{ backgroundColor: '#ffffff', border: `1px solid ${PANEL_BORDER}`, color: TEXT_PRIMARY }}
                          labelStyle={{ color: TEXT_PRIMARY, fontWeight: 700 }}
                        />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        {algorithmStreamConvergenceEntries.map(([name], index) => {
                          const rowColor =
                            algorithmStreamRows.find((row) => row.name === name)?.color ??
                            FALLBACK_COLORS[index % FALLBACK_COLORS.length];
                          return (
                            <Area
                              key={name}
                              type="monotone"
                              dataKey={name}
                              stroke={rowColor}
                              fill={`url(#stream-modal-${toGradientId(name)})`}
                              strokeWidth={2}
                              dot={false}
                              connectNulls
                              isAnimationActive={false}
                            />
                          );
                        })}
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : (
                    <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography variant="body2" sx={{ color: TEXT_SECONDARY }}>
                        Waiting for convergence samples.
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Paper>

              {algorithmStreamRows.length > 0 && (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small" sx={{ minWidth: hasAlgorithmStatistics ? 1180 : 920 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 800 }}>Algorithm</TableCell>
                        <TableCell sx={{ fontWeight: 800 }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 800 }}>Fitness</TableCell>
                        <TableCell sx={{ fontWeight: 800 }}>Agg. QoS</TableCell>
                        <TableCell sx={{ fontWeight: 800 }}>Energy</TableCell>
                        <TableCell sx={{ fontWeight: 800 }}>Cost</TableCell>
                        <TableCell sx={{ fontWeight: 800 }}>Response</TableCell>
                        <TableCell sx={{ fontWeight: 800 }}>Execution</TableCell>
                        <TableCell sx={{ fontWeight: 800 }}>Samples</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {algorithmStreamRows.map((row) => (
                        <TableRow key={row.name} hover>
                          <TableCell sx={{ fontWeight: 800, color: row.color }}>{row.name}</TableCell>
                          <TableCell>{row.status}</TableCell>
                          {renderAlgorithmMetricTableCell(row, 'fitness', '', 4)}
                          {renderAlgorithmMetricTableCell(row, 'aggregatedQos', '', 4)}
                          {renderAlgorithmMetricTableCell(row, 'energy', 'J', 4)}
                          {renderAlgorithmMetricTableCell(row, 'cost', '', 4)}
                          {renderAlgorithmMetricTableCell(row, 'responseTime', 'ms', 2)}
                          {renderAlgorithmMetricTableCell(row, 'executionTime', 'ms', 2)}
                          <TableCell>{row.sampleCount}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Stack>
          ) : (
            <Box
              sx={{
                minHeight: 280,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 2,
                border: `1px dashed ${SOFT_BORDER}`,
                backgroundColor: PANEL_BG_SUBTLE,
              }}
            >
              <Typography variant="body2" sx={{ color: TEXT_SECONDARY }}>
                No Algorithm Stream data has been saved for this run yet.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRunningAlgorithmModalOpen(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={selectedIteration !== null}
        onClose={() => setSelectedIteration(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          Iteration {selectedIteration} Explanation
        </DialogTitle>
        <DialogContent dividers>
          {selectedSnapshotRows.length > 0 ? (
            <Stack spacing={1.5}>
              <Typography variant="body2" sx={{ color: TEXT_SECONDARY }}>
                This snapshot ranks the algorithms by fitness at iteration {selectedIteration}. Higher fitness is better.
                {approximateSelectedCount > 0
                  ? ` ${approximateSelectedCount} series use the nearest available point because they do not have an exact sample at this iteration.`
                  : ' All series have exact values at this iteration.'}
              </Typography>
              {bestSelectedSnapshot && worstSelectedSnapshot && (
                <Typography variant="body2" sx={{ color: TEXT_PRIMARY }}>
                  <Box component="span" sx={{ fontWeight: 700, color: bestSelectedSnapshot.color }}>
                    {bestSelectedSnapshot.name}
                  </Box>
                  {' '}is leading with {bestSelectedSnapshot.value.toFixed(4)}, while{' '}
                  <Box component="span" sx={{ fontWeight: 700, color: worstSelectedSnapshot.color }}>
                    {worstSelectedSnapshot.name}
                  </Box>
                  {' '}is lowest at {worstSelectedSnapshot.value.toFixed(4)}. The spread is {selectedSnapshotSpread.toFixed(4)}.
                </Typography>
              )}
              <Stack spacing={0.8}>
                {selectedSnapshotRows.map((row, index) => {
                  const normalizedWidth =
                    bestSelectedSnapshot && worstSelectedSnapshot && selectedSnapshotSpread > 0
                      ? 18 + ((row.value - worstSelectedSnapshot.value) / selectedSnapshotSpread) * 82
                      : 100;
                  return (
                    <Box
                      key={`${row.name}-${row.sourceIteration}`}
                      sx={{
                        p: 1,
                        borderRadius: 2,
                        backgroundColor: `${row.color}12`,
                        border: `1px solid ${row.color}30`,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                        <Typography variant="body2" sx={{ fontWeight: 800, color: row.color }}>
                          #{index + 1} {row.name}
                        </Typography>
                        <Typography variant="caption" sx={{ ml: 'auto', color: TEXT_SECONDARY }}>
                          {row.isExact ? 'Exact point' : `Nearest point from iteration ${row.sourceIteration}`}
                        </Typography>
                      </Box>
                      <Box sx={{ height: 7, borderRadius: 999, overflow: 'hidden', backgroundColor: `${row.color}20`, mb: 0.6 }}>
                        <Box
                          sx={{
                            width: `${normalizedWidth}%`,
                            height: '100%',
                            borderRadius: 999,
                            backgroundColor: row.color,
                          }}
                        />
                      </Box>
                      <Typography variant="caption" sx={{ color: TEXT_PRIMARY, fontFamily: 'monospace', fontWeight: 700 }}>
                        Fitness: {row.value.toFixed(4)}
                      </Typography>
                    </Box>
                  );
                })}
              </Stack>
            </Stack>
          ) : (
            <Typography variant="body2" sx={{ color: TEXT_SECONDARY }}>
              No convergence data is available for this iteration yet.
            </Typography>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

const EnergySimulationPanel: React.FC = () => {
  const { results, isRunning, completedAlgorithms, currentAlgorithm } = useOptimization();

  const algorithms = useMemo(() => {
    const names: string[] = [];
    const includeBaseline = Boolean(results?.configuration?.includeSflaGaBaseline ?? results?.sflaGa);
    const keys: ResultAlgorithmKey[] = includeBaseline
      ? ['ga', 'culture', 'frog', 'sflaGa', 'sflaga', 'pso', 'gapso']
      : ['ga', 'culture', 'frog', 'sflaga', 'pso', 'gapso'];
    keys.forEach((k) => names.push(RESULT_ALGORITHM_NAMES[k] || RESULT_ALGORITHM_NAMES[k as ResultAlgorithmKey] || k.toUpperCase()));
    return names;
  }, [results]);

  const data = useMemo(() => {
    return algorithms.map((alg) => {
      const key = NAME_TO_KEY[alg];
      const energy = key && results && (results as any)[key]?.energy;
      return { name: alg, energy: energy ?? null };
    });
  }, [algorithms, results]);

  const activeAlgorithms = useMemo(() => new Set(extractAlgorithmNames(currentAlgorithm)), [currentAlgorithm]);

  const hasValues = data.some((d) => d.energy !== null && d.energy !== undefined);

  const getColor = (name: string, index: number) => {
    const key = NAME_TO_KEY[name];
    return (key && RESULT_ALGORITHM_COLORS[key]) || FALLBACK_COLORS[index % FALLBACK_COLORS.length];
  };

  return (
    <Box sx={{ color: TEXT_PRIMARY }}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Paper
            elevation={3}
            sx={{
              p: 2.5,
              background: PANEL_BG_SUBTLE,
              border: `1px solid ${PANEL_BORDER}`,
              color: TEXT_PRIMARY,
            }}
          >
            <Stack spacing={1.25}>
              <Typography variant="h6" fontWeight={700}>
                Energy Snapshot
              </Typography>
              <Typography variant="body2" sx={{ color: TEXT_SECONDARY }}>
                Updates as each algorithm finishes. Active algorithms flash until they complete.
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {algorithms.map((alg, idx) => {
                  const isDone = completedAlgorithms.has(alg) && Boolean(data[idx]?.energy !== null);
                  const isActive = activeAlgorithms.has(alg) && isRunning;
                  const color = getColor(alg, idx);
                  return (
                    <Chip
                      key={alg}
                      label={alg}
                      size="small"
                      variant={isDone ? 'filled' : 'outlined'}
                      sx={{
                        color: isDone ? '#ffffff' : TEXT_PRIMARY,
                        backgroundColor: isDone ? color : 'transparent',
                        borderColor: isActive ? color : SOFT_BORDER,
                        fontWeight: isActive ? 700 : 500,
                        animation: isActive ? `${flash} 1.6s ease-in-out infinite` : 'none',
                        boxShadow: isActive ? `0 0 0 1px ${color}` : 'none',
                      }}
                      icon={isActive ? <AutoAwesomeIcon fontSize="small" sx={{ color }} /> : undefined}
                    />
                  );
                })}
              </Stack>
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper
            elevation={4}
            sx={{
              p: 2.5,
              height: '100%',
              background: PANEL_BG,
              border: `1px solid ${PANEL_BORDER}`,
              color: TEXT_PRIMARY,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Box>
                <Typography variant="subtitle1" fontWeight={700}>
                  Energy by Algorithm (lower is better)
                </Typography>
                <Typography variant="body2" sx={{ color: TEXT_SECONDARY }}>
                  Fills as algorithms complete during the run.
                </Typography>
              </Box>
              <Chip
                label={isRunning ? 'Streaming' : hasValues ? 'Complete' : 'Waiting'}
                color={isRunning ? 'success' : hasValues ? 'default' : 'warning'}
                size="small"
                sx={{
                  color: isRunning ? '#166534' : hasValues ? TEXT_PRIMARY : '#92400e',
                  backgroundColor: isRunning
                    ? 'rgba(34,197,94,0.12)'
                    : hasValues
                      ? 'rgba(148,163,184,0.12)'
                      : 'rgba(245,158,11,0.14)',
                }}
              />
            </Box>

            <Box sx={{ height: 260 }}>
              {hasValues ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 16 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
                    <XAxis dataKey="name" stroke={AXIS_STROKE} tick={{ fontSize: 12, fill: AXIS_STROKE }} />
                    <YAxis stroke={AXIS_STROKE} tick={{ fontSize: 12, fill: AXIS_STROKE }} />
                    <RechartsTooltip
                      contentStyle={{ backgroundColor: '#ffffff', border: `1px solid ${PANEL_BORDER}`, color: TEXT_PRIMARY }}
                      labelStyle={{ color: TEXT_PRIMARY }}
                      itemStyle={{ color: TEXT_PRIMARY }}
                      formatter={(v: any) => (v === null ? 'pending' : `${Number(v).toFixed(4)} J`)}
                    />
                    <Bar dataKey="energy" radius={6}>
                      {data.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={getColor(entry.name, index)}
                          stroke="rgba(15,23,42,0.12)"
                          strokeWidth={0}
                          cursor="pointer"
                          onMouseEnter={(e: any) => {
                            if (e && e.target) e.target.setAttribute('opacity', '0.8');
                          }}
                          onMouseLeave={(e: any) => {
                            if (e && e.target) e.target.setAttribute('opacity', '1');
                          }}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: TEXT_SECONDARY, flexDirection: 'column', gap: 1 }}>
                  <CircularProgress size={28} sx={{ color: '#fcd34d' }} />
                  <Typography variant="body2">Waiting for energy results...</Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SimulationPanel;
export { EnergySimulationPanel };
